import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  User,
  Building2,
  StickyNote,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  TrendingUp,
  Facebook,
  Instagram,
  Globe,
  PhoneCall,
  FileText,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";

const SOURCE_ICONS = {
  meta_ads: Facebook,
  facebook: Facebook,
  instagram: Instagram,
  appel: PhoneCall,
  email: Mail,
  site_web: Globe,
  recommandation: Users,
  autre: MessageSquare
};

const SOURCE_LABELS = {
  meta_ads: "Meta Ads",
  facebook: "Facebook",
  instagram: "Instagram",
  appel: "Appel",
  email: "Email",
  site_web: "Site Web",
  recommandation: "Recommandation",
  autre: "Autre"
};

const STATUS_CONFIG = {
  nouveau: { color: "bg-blue-500", label: "🆕 Nouveau", icon: Clock },
  rdv_pris: { color: "bg-green-500", label: "📅 RDV pris !", icon: CheckCircle },
  message_laisse: { color: "bg-yellow-500", label: "📞 Message laissé", icon: Phone },
  devis_envoye: { color: "bg-purple-500", label: "📄 Devis envoyé !", icon: FileText },
  non_qualifie: { color: "bg-gray-500", label: "❌ Non qualitatif", icon: XCircle },
  converti: { color: "bg-emerald-500", label: "✅ Converti", icon: CheckCircle },
  supprime: { color: "bg-red-500", label: "🗑️ Supprimé", icon: Trash2 }
};

export default function LeadsPage() {
  const { user, agencies } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(user?.agency_id || "all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  
  const [newLead, setNewLead] = useState({
    name: "",
    phone: "",
    email: "",
    source: "appel",
    vehicle: "",
    prestation_souhaitee: "",
    agency_id: user?.agency_id || ""
  });

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchSourcesAndStatuses();
  }, [selectedAgency, selectedStatus, selectedSource]);

  useEffect(() => {
    if (selectedLead) {
      fetchNotes(selectedLead.id);
    }
  }, [selectedLead]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let params = [];
      if (selectedAgency !== "all") params.push(`agency_id=${selectedAgency}`);
      if (selectedStatus !== "all") params.push(`status=${selectedStatus}`);
      if (selectedSource !== "all") params.push(`source=${selectedSource}`);
      
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      const res = await axios.get(`${API}/leads${queryString}`);
      setLeads(res.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const agencyParam = selectedAgency !== "all" ? `?agency_id=${selectedAgency}` : "";
      const res = await axios.get(`${API}/leads/stats/summary${agencyParam}`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchSourcesAndStatuses = async () => {
    try {
      const [sourcesRes, statusesRes] = await Promise.all([
        axios.get(`${API}/lead-sources`),
        axios.get(`${API}/lead-statuses`)
      ]);
      setSources(sourcesRes.data.sources);
      setStatuses(statusesRes.data.statuses);
    } catch (error) {
      console.error("Failed to fetch sources/statuses:", error);
    }
  };

  const fetchNotes = async (leadId) => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/leads/${leadId}/notes`);
      setNotes(res.data);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleCreateLead = async () => {
    try {
      if (!newLead.name || !newLead.agency_id) {
        toast.error("Veuillez remplir le nom et l'agence");
        return;
      }
      
      await axios.post(`${API}/leads`, newLead);
      toast.success("Lead créé avec succès");
      setShowAddDialog(false);
      fetchLeads();
      fetchStats();
      resetNewLead();
    } catch (error) {
      toast.error("Erreur lors de la création du lead");
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/notes`, {
        content: newNote,
        lead_id: selectedLead.id
      });
      toast.success("Note ajoutée");
      setNewNote("");
      fetchNotes(selectedLead.id);
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead supprimé");
      fetchLeads();
      fetchStats();
      setShowDetailSheet(false);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetNewLead = () => {
    setNewLead({
      name: "",
      phone: "",
      email: "",
      source: "appel",
      vehicle: "",
      prestation_souhaitee: "",
      agency_id: user?.agency_id || ""
    });
  };

  const filteredLeads = leads.filter(lead => {
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.phone?.includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.vehicle?.toLowerCase().includes(query)
    );
  });

  const getAgencyName = (agencyId) => {
    if (!agencyId) return "À attribuer";
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "—";
  };

  const handleAssignAgency = async (leadId, agencyId) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { agency_id: agencyId });
      toast.success(`Lead attribué à ${getAgencyName(agencyId)}`);
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) setSelectedLead({ ...selectedLead, agency_id: agencyId });
    } catch (error) {
      toast.error("Erreur lors de l'attribution");
    }
  };

  const getSourceIcon = (source) => {
    const Icon = SOURCE_ICONS[source] || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
          >
            LEADS / PROSPECTS
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredLeads.length} lead{filteredLeads.length > 1 ? 's' : ''} • Taux de conversion: {stats?.conversion_rate || 0}%
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                style={{ background: '#FEED00', color: '#000' }}
                data-testid="add-lead-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Lead
              </Button>
            </DialogTrigger>
            <DialogContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}>
                  NOUVEAU LEAD
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  Ajouter un nouveau prospect
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Nom *</Label>
                  <Input
                    value={newLead.name}
                    onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="bg-black border-gray-800 text-white"
                    data-testid="new-lead-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Téléphone</Label>
                    <Input
                      value={newLead.phone}
                      onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                      placeholder="+33 6..."
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-lead-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Email</Label>
                    <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                      placeholder="email@..."
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-lead-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Source *</Label>
                  <Select 
                    value={newLead.source} 
                    onValueChange={(v) => setNewLead({ ...newLead, source: v })}
                  >
                    <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-lead-source">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      {sources.map((src) => (
                        <SelectItem key={src} value={src} className="text-gray-200">
                          {SOURCE_LABELS[src] || src}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Véhicule</Label>
                  <Input
                    value={newLead.vehicle}
                    onChange={(e) => setNewLead({ ...newLead, vehicle: e.target.value })}
                    placeholder="BMW 320d"
                    className="bg-black border-gray-800 text-white"
                    data-testid="new-lead-vehicle"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Prestation souhaitée</Label>
                  <Input
                    value={newLead.prestation_souhaitee}
                    onChange={(e) => setNewLead({ ...newLead, prestation_souhaitee: e.target.value })}
                    placeholder="Stage 1, E85..."
                    className="bg-black border-gray-800 text-white"
                    data-testid="new-lead-prestation"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Agence *</Label>
                  <Select 
                    value={newLead.agency_id} 
                    onValueChange={(v) => setNewLead({ ...newLead, agency_id: v })}
                  >
                    <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-lead-agency">
                      <SelectValue placeholder="Sélectionner une agence" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                          {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleCreateLead}
                    className="flex-1"
                    style={{ background: '#FEED00', color: '#000' }}
                    data-testid="confirm-new-lead-btn"
                  >
                    Créer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(STATUS_CONFIG).map(([statusKey, config]) => {
            const count = stats.by_status?.[statusKey] || 0;
            const Icon = config.icon;
            return (
              <Card 
                key={statusKey}
                className="cursor-pointer transition-all hover:border-[#FEED00]"
                style={{ background: '#0A0A0A', borderColor: selectedStatus === statusKey ? '#FEED00' : '#1A1A1A' }}
                onClick={() => setSelectedStatus(selectedStatus === statusKey ? "all" : statusKey)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${config.color}`} />
                    <span className="text-xs text-gray-500">{config.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {count}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black border-gray-800 text-white"
            data-testid="search-leads"
          />
        </div>

        {user?.role === "admin" && (
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger className="w-[140px] bg-black border-gray-800 text-white" data-testid="leads-agency-filter">
              <Building2 className="w-4 h-4 mr-2 text-gray-600" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
              <SelectItem value="all" className="text-gray-200">Toutes</SelectItem>
              {agencies.map((agency) => (
                <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                  {agency.city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedSource} onValueChange={setSelectedSource}>
          <SelectTrigger className="w-[140px] bg-black border-gray-800 text-white" data-testid="leads-source-filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
            <SelectItem value="all" className="text-gray-200">Toutes sources</SelectItem>
            {sources.map((src) => (
              <SelectItem key={src} value={src} className="text-gray-200">
                {SOURCE_LABELS[src] || src}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => { setSelectedStatus("all"); setSelectedSource("all"); setSearchQuery(""); }}
          className="border-gray-700 text-gray-400 hover:text-white"
        >
          Réinitialiser
        </Button>
      </div>

      {/* Leads Table */}
      <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: '#1A1A1A' }} className="hover:bg-transparent">
                <TableHead className="text-gray-500">Lead</TableHead>
                <TableHead className="text-gray-500">Source</TableHead>
                <TableHead className="text-gray-500">Véhicule</TableHead>
                <TableHead className="text-gray-500 hidden md:table-cell">Agence</TableHead>
                <TableHead className="text-gray-500">Statut</TableHead>
                <TableHead className="text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} style={{ borderColor: '#1A1A1A' }}>
                    <TableCell colSpan={6}>
                      <div className="h-12 rounded skeleton" style={{ background: '#1A1A1A' }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredLeads.length === 0 ? (
                <TableRow style={{ borderColor: '#1A1A1A' }}>
                  <TableCell colSpan={6} className="text-center py-12 text-gray-600">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun lead trouvé</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeads.map((lead) => {
                  const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.nouveau;
                  return (
                    <TableRow 
                      key={lead.id} 
                      style={{ borderColor: '#1A1A1A' }}
                      className="table-row-hover cursor-pointer"
                      onClick={() => {
                        setSelectedLead(lead);
                        setShowDetailSheet(true);
                      }}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: '#1A1A1A' }}
                          >
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{lead.name}</p>
                            <p className="text-sm text-gray-600">
                              {lead.phone || lead.email || "Pas de contact"}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-gray-400">
                          {getSourceIcon(lead.source)}
                          <span className="text-sm">{SOURCE_LABELS[lead.source] || lead.source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {lead.vehicle || "—"}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {!lead.agency_id ? (
                          <Badge variant="outline" className="gap-1.5 border-amber-500/50 text-amber-400 bg-amber-500/10" data-testid={`lead-unassigned-${lead.id}`}>
                            <AlertTriangle className="w-3 h-3" />
                            À attribuer
                          </Badge>
                        ) : (() => {
                          const color = getAgencyColorByAgency(agencies.find(a => a.id === lead.agency_id));
                          return (
                            <Badge variant="outline" className="gap-1.5" style={{ borderColor: color.border, color: color.text, background: color.bg }}>
                              <AgencyDot color={color} className="w-2 h-2" />
                              {getAgencyName(lead.agency_id)}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${statusConfig.color}`} />
                          <span className="text-sm text-gray-300">{statusConfig.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-[#FEED00]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLead(lead);
                            setShowDetailSheet(true);
                          }}
                        >
                          <StickyNote className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Lead Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="w-full sm:max-w-lg overflow-y-auto">
          {selectedLead && (
            <>
              <SheetHeader>
                <SheetTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }} className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  FICHE LEAD
                </SheetTitle>
                <SheetDescription className="text-gray-500">
                  {selectedLead.name}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Contact Info */}
                <div className="p-4 rounded-lg space-y-3" style={{ background: '#111' }}>
                  {selectedLead.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="text-white font-mono">{selectedLead.phone}</p>
                      </div>
                    </div>
                  )}
                  {selectedLead.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-white">{selectedLead.email}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    {getSourceIcon(selectedLead.source)}
                    <div>
                      <p className="text-sm text-gray-500">Source</p>
                      <p className="text-white">{SOURCE_LABELS[selectedLead.source] || selectedLead.source}</p>
                    </div>
                  </div>
                  {selectedLead.vehicle && (
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm text-gray-500">Véhicule / Prestation</p>
                        <p className="text-white">{selectedLead.vehicle} {selectedLead.prestation_souhaitee && `- ${selectedLead.prestation_souhaitee}`}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Agence / attribution */}
                {(user?.role === "admin" || !selectedLead.agency_id) && (
                  <div className={`space-y-2 p-3 rounded-lg border ${!selectedLead.agency_id ? 'border-amber-500/40 bg-amber-500/5' : 'border-gray-800'}`} data-testid="lead-agency-section">
                    <p className="text-sm text-gray-500 font-medium flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {selectedLead.agency_id ? "Centre" : "Ce lead n'est attribué à aucun centre"}
                    </p>
                    <Select value={selectedLead.agency_id || ""} onValueChange={(v) => handleAssignAgency(selectedLead.id, v)}>
                      <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="lead-assign-agency">
                        <SelectValue placeholder="Choisir un centre..." />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                        {agencies.map((agency) => (
                          <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                            <span className="flex items-center gap-2">
                              <AgencyDot color={getAgencyColorByAgency(agency)} />
                              {agency.city}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Status Update */}
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 font-medium">Changer le statut</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(STATUS_CONFIG).map(([statusKey, config]) => {
                      const Icon = config.icon;
                      const isActive = selectedLead.status === statusKey;
                      return (
                        <Button
                          key={statusKey}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedLead.id, statusKey)}
                          className={`justify-start ${isActive 
                            ? "bg-[#FEED00] text-black hover:bg-[#FFF44F]" 
                            : "border-gray-700 text-gray-400 hover:border-[#FEED00] hover:text-[#FEED00]"
                          }`}
                          data-testid={`status-${statusKey}`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          <span className="text-xs">{config.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notes ({notes.length})
                  </h3>
                  
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Ajouter une note..."
                      className="bg-black border-gray-800 text-white resize-none"
                      rows={2}
                      data-testid="new-lead-note-input"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      style={{ background: '#FEED00', color: '#000' }}
                      className="px-3"
                      data-testid="add-lead-note-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {loadingNotes ? (
                      <div className="text-center py-4 text-gray-600">Chargement...</div>
                    ) : notes.length === 0 ? (
                      <div className="text-center py-4 text-gray-600 text-sm">
                        Aucune note
                      </div>
                    ) : (
                      notes.map((note) => (
                        <div 
                          key={note.id}
                          className="p-3 rounded-lg border"
                          style={{ background: '#111', borderColor: '#1A1A1A' }}
                        >
                          <p className="text-gray-300 text-sm whitespace-pre-wrap">{note.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                            <span>{note.user_name}</span>
                            <span>•</span>
                            <span>{format(new Date(note.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-4 border-t" style={{ borderColor: '#1A1A1A' }}>
                  <Button
                    variant="ghost"
                    onClick={() => handleDeleteLead(selectedLead.id)}
                    className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="delete-lead-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer le lead
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
